import map from 'lodash/map'
import React, { useEffect, useMemo } from 'react'
import {
  Row,
  useColumnOrder,
  useFilters,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table'
import { Grid } from '@material-ui/core'

import Loading from 'components/Loading'
import useGroupsCollection from '../hooks/useGroupsCollection'
import { useEnabledGroups } from 'Event/components/opportunity/GroupPicker/hooks'
import { RouteComponentProps } from 'react-router'
import PageHeader from 'App/components/PageHeader'
import { ExtendedPagedTable } from 'core/table/components'
import ContainedButton from 'civic-champs-shared/core/ContainedButton'
import AddIcon from '@material-ui/icons/Add'
import ArchiveOutlinedIcon from '@material-ui/icons/ArchiveOutlined'
import UnarchiveOutlinedIcon from '@material-ui/icons/UnarchiveOutlined'
import { Group } from 'Event/components/opportunity/GroupPicker/types'
import useGetColumnState from 'core/table/table-hooks/useGetColumnState'
import { useConditionalSelectColumn } from 'core/table/table-hooks'
import DEFAULT_FILTERS, { useGetGlobalFilter } from 'core/table/filters'
import useGroupOverviewColumns, { useStyles } from 'group/hooks/useGroupOverviewColumns'
import ExportGroupsButton from 'group/components/ExportGroupsButton'
import FeatureEnabled from 'core/feature/components/FeatureEnabled'
import { TableMenuButton } from 'civic-champs-shared'
import { find, get, isBoolean, some } from 'lodash'
import { useFeatureEnabled } from 'civic-champs-shared/feature/hooks'
import { useHasRole } from 'civic-champs-shared/auth/hooks'
import { INTERNAL_ADMIN } from 'civic-champs-shared/auth/utils/permissions'
import { GroupOverviewFilterButton } from 'group/components/GroupOverviewFilterButton'

const tableName = 'groups'
const getOperatorOptions = (id: string) => []

const FILTER_TYPES = {
  ...DEFAULT_FILTERS,
  isNull: (rows: Row[], ids: string[], filterValue: any) => {
    if (!isBoolean(filterValue)) return rows

    return rows.filter(row =>
      ids.some(id => {
        const value = row.values[id]
        return (filterValue === true && !value) || (filterValue === false && !!value)
      }),
    )
  },
}
const initialFilters = [{ id: 'archivedAt', value: true }]

export const GroupsOverviewComponent = ({ location, history }: RouteComponentProps) => {
  const skipReset = React.useRef<boolean>()
  const [{ groups, initiallyLoaded }, { addGroup, archiveGroups }, eventListeners] = useGroupsCollection()
  const classes = useStyles()
  const [fetchColumnState, { loading: columnStateLoading, result: columnState }] = useGetColumnState()

  const filteredGroups = useEnabledGroups(groups as Group[], true)

  const columns = useGroupOverviewColumns()
  const globalFilter = useGetGlobalFilter({
    columns,
    filterTypes: FILTER_TYPES,
    getOperatorOptions,
  })

  useEffect(() => {
    fetchColumnState(tableName)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const isInternalAdmin = useHasRole(INTERNAL_ADMIN)
  const canHideGroupsEnabled = useFeatureEnabled('CanHideGroups')
  const canArchiveGroups = isInternalAdmin && canHideGroupsEnabled
  const initialTableState = useMemo(
    () => ({
      hiddenColumns: canArchiveGroups ? [] : ['archivedAt'],
      globalFilter: '',
      filters: initialFilters,
    }),
    [canArchiveGroups],
  )

  const table = useTable(
    {
      initialState: initialTableState,
      data: filteredGroups,
      columns,
      //@ts-ignore
      globalFilter,
      filterTypes: FILTER_TYPES,
      autoResetSortBy: !skipReset.current,
      autoResetPage: !skipReset.current,
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
    useColumnOrder,
    useConditionalSelectColumn(() => true, classes.selectColumn),
  )

  const filterButtonProps = {
    // @ts-ignore
    showArchivedGroups: !get(find(table.state.filters, { id: 'archivedAt' }), 'value', undefined),
    setShowArchivedGroups: (enabled: boolean) => {
      // @ts-ignore
      table.setFilter('archivedAt', enabled ? null : true)
    },
  }

  const [selectedRows, selectedRowsCount] = useMemo(() => {
    return [table.selectedFlatRows, table.selectedFlatRows.length]
  }, [table.selectedFlatRows])

  if (!initiallyLoaded || columnStateLoading) {
    return <Loading />
  }

  return (
    <Grid container>
      <ExtendedPagedTable
        history={history}
        location={location}
        table={table}
        columns={columns}
        columnState={columnState}
        tableName={tableName}
        searchPlaceholder="Search Groups"
        filterSubHeader="Select volunteer attributes to filter by:"
        buttons={
          <>
            <FeatureEnabled code="CanHideGroups">
              <TableMenuButton
                startIcon={<ArchiveOutlinedIcon />}
                onClick={() => archiveGroups(map(selectedRows, 'original'), true)}
                disabled={!selectedRowsCount}
                rightMargin
              >
                Archive
              </TableMenuButton>
              {some(selectedRows, (row: any) => row.original.archivedAt != null) && (
                <TableMenuButton
                  startIcon={<UnarchiveOutlinedIcon />}
                  onClick={() => archiveGroups(map(selectedRows, 'original'), false)}
                  disabled={!selectedRowsCount}
                  rightMargin
                >
                  Unarchive
                </TableMenuButton>
              )}
            </FeatureEnabled>
            <ExportGroupsButton data={selectedRows} disabled={!selectedRowsCount} />
          </>
        }
        addButton={
          <ContainedButton startIcon={<AddIcon />} onClick={addGroup}>
            Add Group
          </ContainedButton>
        }
        useGlobalSearch
        useFilters
        filterButton={canArchiveGroups ? <GroupOverviewFilterButton {...filterButtonProps} /> : undefined}
        eventListeners={eventListeners}
        skipReset={skipReset}
      />
    </Grid>
  )
}

export const GroupsOverview = (props: RouteComponentProps) => {
  return (
    <PageHeader title="Groups" subTitle="Create and manage volunteer groups">
      <GroupsOverviewComponent {...props} />
    </PageHeader>
  )
}
